import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const RESEND_TIMEOUT = 60; // seconds

const VerifyOtp = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, get email from location.state or localStorage
  useEffect(() => {
    let emailFromState = location.state && location.state.email;
    let emailFromStorage = localStorage.getItem("verifyOtpEmail");
    if (emailFromState) {
      setEmail(emailFromState);
      localStorage.setItem("verifyOtpEmail", emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    }
  }, [location.state]);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        import.meta.env.VITE_APP_BASE_API +
          import.meta.env.VITE_APP_API_VERSION +
          "/user/verify-otp",
        { email: email.toLowerCase(), otp }
      );
      if (res.data.success) {
        setSuccess("Email verified! You can now log in.");
        toast.success("Email verified! Please sign in using your credentials.");
        localStorage.removeItem("verifyOtpEmail");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setError(res.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        import.meta.env.VITE_APP_BASE_API +
          import.meta.env.VITE_APP_API_VERSION +
          "/user/resend-otp",
        { email: email.toLowerCase() }
      );
      if (res.data.success) {
        setSuccess("OTP resent! Please check your email.");
        setResendTimer(RESEND_TIMEOUT);
      } else {
        setError(res.data.message || "Could not resend OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#141414] px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center"
        style={{ boxShadow: "0 4px 32px 0 rgba(0,0,0,0.18)" }}
      >
        <a href="/login">
        <img
          src="https://smartnurse.s3.us-east-1.amazonaws.com/public/logo-bw.png"
          alt="SmartNurse"
          className="mx-auto mb-4"
          style={{ width: 200 }}
        />
        </a>
        <p className="text-gray-700 mb-6 text-sm">
          Enter the code below to verify your email for your account.
        </p>
        <div className="mb-4">
          <input
            type="email"
            className="w-full border rounded px-3 py-2 text-center text-lg bg-gray-100"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              localStorage.setItem("verifyOtpEmail", e.target.value);
            }}
            required
            disabled={!!location.state?.email}
          />
        </div>
        <div className="mb-6">
          <input
            type="text"
            className="w-full border-2 border-[#4f84f8] rounded px-3 py-3 text-center text-lg tracking-widest font-bold bg-gray-50"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
            maxLength={6}
            pattern="[0-9]{6}"
            inputMode="numeric"
            placeholder="Enter 6-digit code"
            style={{ letterSpacing: 8 }}
          />
        </div>

        <div>
            <button
            type="submit"
            className="w-full mb-4 bg-[#4f84f8] text-white py-2 rounded-full font-semibold hover:bg-[#b0060f] transition"
            disabled={loading}
            >
            {loading ? "Verifying..." : "Verify"}
            </button>
            <span className="text-xs text-gray-500">
            This code will expire in 10 minutes.
            </span>
            {error && <div className="text-red-500 mb-2">{error}</div>}
            {success && <div className="text-green-600 mb-2">{success}</div>}
        </div>

        <div className="mb-4 flex flex-col items-center">
          <button
            type="button"
            className={`mb-2 w-full py-2 rounded-full bg-[#dbdbdb] text-black font-semibold transition ${
              resendTimer > 0 || resendLoading
                ? "opacity-60 cursor-not-allowed"
                : "hover:bg-[#48a329] hover:text-white"
            }`}
            onClick={handleResend}
            disabled={resendTimer > 0 || resendLoading}
          >
            {resendLoading
              ? "Sending..."
              : resendTimer > 0
              ? `Resend OTP (${resendTimer}s)`
              : "Resend OTP"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VerifyOtp;
